// Pure composer math: z-order, canvas text layout, program-link upsert.
// Keep free of DOM/React so vitest covers it without a browser.
import type { FlyerDocElement } from '@/types/flyerDoc';
import type { FlyerStyleConfig } from '@/types/flyer';
import type { TopicDetails } from '@/types/topic';
import { readLinksOfType, writeLinksOfType } from '@/types/topic';

export const zSorted = (els: FlyerDocElement[]) => [...els].sort((a, b) => a.z - b.z);

export const nextZ = (els: FlyerDocElement[]) => els.reduce((m, e) => Math.max(m, e.z), 0) + 1;

export function bumpZ(els: FlyerDocElement[], id: string, dir: 1 | -1): FlyerDocElement[] {
  const order = zSorted(els);
  const i = order.findIndex((e) => e.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return els;
  const zi = order[i].z;
  const zj = order[j].z;
  return els.map((e) => (e.id === order[i].id ? { ...e, z: zj } : e.id === order[j].id ? { ...e, z: zi } : e));
}

export function styleToFont(style: FlyerStyleConfig | undefined): string {
  const size = style?.fontSize ?? 48;
  const family = style?.fontName && style.fontName !== 'System' ? `"${style.fontName}", sans-serif` : 'sans-serif';
  const parts = [];
  if (style?.isItalic) parts.push('italic');
  if (style?.fontWeight === 'bold') parts.push('bold');
  parts.push(`${size}px`, family);
  return parts.join(' ');
}

export function wrapLines(measure: (s: string) => number, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    if (para === '') { if (text !== '') out.push(''); continue; }
    let line = '';
    const push = () => { if (line !== '') { out.push(line); line = ''; } };
    for (const word of para.split(' ')) {
      const candidate = line === '' ? word : `${line} ${word}`;
      if (measure(candidate) <= maxWidth) { line = candidate; continue; }
      push();
      if (measure(word) <= maxWidth) { line = word; continue; }
      // Word wider than the box (CJK or long token): per-character wrap.
      for (const ch of word) {
        const c = line + ch;
        if (line !== '' && measure(c) > maxWidth) { out.push(line); line = ch; } else line = c;
      }
    }
    push();
  }
  return out;
}

export function alignedX(align: string | undefined, boxX: number, boxW: number): { x: number; textAlign: 'left' | 'center' | 'right' } {
  if (align === 'center') return { x: boxX + boxW / 2, textAlign: 'center' };
  if (align === 'right') return { x: boxX + boxW, textAlign: 'right' };
  return { x: boxX, textAlign: 'left' };
}

export function upsertFlyerLink(
  details: TopicDetails,
  { lang, name, url, prevUrl }: { lang: string; name: string; url: string; prevUrl?: string | null },
): TopicDetails {
  const rows = readLinksOfType(details, 'flyer').map((l) => ({ ...l }));
  const i = prevUrl ? rows.findIndex((l) => l.url === prevUrl) : -1;
  const row = { url, name, type: 'flyer', lang };
  if (i >= 0) rows[i] = row; else rows.push(row);
  return writeLinksOfType(details, 'flyer', rows);
}
